'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';

interface UpdateRefreshSettingsParams {
  botId: string;
  contentId: string;
  refreshEnabled: boolean;
  refreshInterval: 'never' | 'daily' | 'weekly' | 'monthly';
}

interface ManualRefreshParams {
  botId: string;
  contentId: string;
}

async function updateRefreshSettings({
  botId,
  contentId,
  refreshEnabled,
  refreshInterval,
}: UpdateRefreshSettingsParams) {
  const res = await fetch(`/api/bots/${botId}/content/${contentId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshEnabled, refreshInterval }),
  });

  if (!res.ok) {
    let message = 'Failed to update refresh settings';
    try {
      const error = await res.json();
      message = error?.error?.message || message;
    } catch {
      // Non-JSON error response
    }
    throw new Error(message);
  }

  return res.json();
}

async function triggerManualRefresh({ botId, contentId }: ManualRefreshParams) {
  const res = await fetch(`/api/bots/${botId}/content/${contentId}/refresh`, {
    method: 'POST',
  });

  if (!res.ok) {
    let message = 'Failed to refresh content';
    try {
      const error = await res.json();
      message = error?.error?.message || message;
    } catch {
      // Non-JSON error response
    }
    throw new Error(message);
  }

  return res.json();
}

export function useUpdateRefreshSettings(botId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Omit<UpdateRefreshSettingsParams, 'botId'>) =>
      updateRefreshSettings({ botId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentSources', botId] });
    },
  });
}

export function useManualRefresh(botId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Omit<ManualRefreshParams, 'botId'>) =>
      triggerManualRefresh({ botId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentSources', botId] });
    },
  });
}
